import React, { useState } from "react";
import { t } from "i18next";
import {
    getOrdinalSuffix,
    groupCharCode,
    getRoundTitle,
    getGroupRoundNumber,
    getGradeName,
    genders,
    COUNTRY_OPTIONS
} from "../../../constants";
import { getOptionName } from "../../../utils/getOptionName";
import "../../../fonts/flag-icons.css";
import {
    getMatchParticipantNames,
    getMatchScoreText,
    formatPlayerName,
    formatDoublesPair,
    formatEntryListName,
    formatEntryListDoublesPair,
    formatTeamMembers,
    formatTournamentLocation
} from "./exportUtils";
import PrintableBracketTree from "./PrintableBracketTree";
import { PRINT_THEME as T } from "./printTheme";

// flag-icons (bundled at src/fonts/flag-icons.css + src/fonts/flags/**) was
// already vendored for a country-flag feature that never got wired up
// anywhere - reused here via its `.fi .fi-<code>` CSS classes (a background-
// image, not an <img>) since that's captured reliably by domToPng, unlike a
// dynamic `require()` over the whole flags folder which returned garbage
// instead of a URL in this webpack setup.
const CountryFlag = ({ code }) => {
    if (!code || typeof code !== "string") return null;
    return <span className={`fi fi-${code.toLowerCase()}`} style={{ flex: "none", borderRadius: "2px", boxShadow: `0 0 0 1px ${T.border}` }} />;
};

const TournamentPrintableTable = ({
    tournament,
    blockKey,
    title,
    subTitle,
    data,
    grade = 1,
    i18n
}) => {
    // Club logos are often hosted on a different origin than the app itself,
    // which doesn't always send back the CORS headers domToPng needs to read
    // the image into the captured canvas - rather than leave a hollow
    // placeholder ring in the exported file when that happens, drop the logo
    // from layout entirely on load failure.
    const [logoFailed, setLogoFailed] = useState(false);

    const club = (typeof tournament?.club_name === 'string' && tournament.club_name.trim())
        || (typeof tournament?.club === 'string' && !/^\d+$/.test(tournament.club.trim()) ? tournament.club.trim() : "");

    const location = formatTournamentLocation(tournament);

    const genderObj = tournament?.gender ? genders.find(g => g.id === tournament.gender) : null;
    const genderName = genderObj ? (tournament.gender === 3 && tournament.grade === 1 ? t("Admixed") : t(genderObj.name)) : null;
    const formatParts = [
        getGradeName(tournament?.grade),
        genderName,
        (typeof tournament?.format === 'string' && !/^\d+$/.test(tournament.format.trim())) ? tournament.format.trim() : null
    ].filter(Boolean);
    const formatStr = formatParts.join(" · ");

    const dateRange = tournament?.start_date && tournament?.end_date
        ? tournament.start_date === tournament.end_date
            ? new Date(tournament.start_date).toLocaleDateString("uk-UA")
            : `${new Date(tournament.start_date).toLocaleDateString("uk-UA")} – ${new Date(tournament.end_date).toLocaleDateString("uk-UA")}`
        : null;

    const rawStage = subTitle || "";
    const cleanStage = (rawStage && rawStage !== t("Participants") && rawStage !== t("Results") && !/^\d+$/.test(String(rawStage).trim()))
        ? rawStage
        : "";

    const renderSchedule = () => {
        const { courts = [], rounds = [] } = data || {};
        if (!courts.length && !rounds.length) {
            return (
                <div style={styles.emptyBox}>
                    {t("No data available")}
                </div>
            );
        }

        const rows = {};
        rounds.forEach(round => {
            if (!rows[round.order]) rows[round.order] = {};
            rows[round.order][round.court] = round;
        });

        return (
            <div style={{ width: "100%", overflowX: "auto" }}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={{ ...styles.th, width: "90px", textAlign: "center" }}>
                                {t("Round")}<br />{t("Time")}
                            </th>
                            {courts.map(court => (
                                <th key={court.id} style={{ ...styles.th, textAlign: "center", minWidth: "160px" }}>
                                    {t("Court")} {court.number || court.name || ""}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {Object.keys(rows).map((order, index) => {
                            const rowCells = Object.values(rows[order]);
                            const firstRoundInRow = rowCells[0];
                            const timeStr = firstRoundInRow?.start_time || "";

                            return (
                                <tr key={order} style={index % 2 === 1 ? styles.trAlt : {}}>
                                    <td style={{ ...styles.td, textAlign: "center", fontWeight: "bold", background: T.superWhite, color: T.ink }}>
                                        <div>№ {index + 1}</div>
                                        {timeStr && <div style={{ fontSize: "12px", color: T.muted, marginTop: "2px" }}>{timeStr}</div>}
                                    </td>
                                    {courts.map(court => {
                                        const round = rows[order]?.[court.id];
                                        const match = round?.match;

                                        if (!match) {
                                            return (
                                                <td key={court.id} style={{ ...styles.td, color: T.muted, textAlign: "center", fontSize: "12px" }}>
                                                    —
                                                </td>
                                            );
                                        }

                                        const { side1, side2 } = getMatchParticipantNames(match, grade);
                                        const scoreText = getMatchScoreText(match);
                                        const groupRound = getGroupRoundNumber(match.total, match.match_order);
                                        const stageDesc = match.group_number
                                            ? `${t("Group")} ${groupCharCode(match.group_number)}${groupRound ? ` (${t("R.")} ${groupRound})` : ""}`
                                            : `${match.draw_number ? `${match.draw_number}${getOrdinalSuffix(match.draw_number)} ${t("Place")} · ` : ""}${getRoundTitle(match.round_number, match.total, t, i18n, match.match_order, false)}`;

                                        return (
                                            <td key={court.id} style={{ ...styles.td, verticalAlign: "top", fontSize: "12px", lineHeight: "1.35" }}>
                                                <div style={{ display: "flex", justifyContent: "space-between", color: T.muted, fontSize: "11px", borderBottom: `1px dashed ${T.border}`, paddingBottom: "3px", marginBottom: "4px", fontWeight: "500" }}>
                                                    <span>{stageDesc}</span>
                                                    {match.match_number && <span>#{match.match_number}</span>}
                                                </div>
                                                <div style={{ fontWeight: match.winner_side === 1 ? "bold" : "500", color: match.winner_side === 1 ? T.darkerGreen : T.ink }}>
                                                    {match.winner_side === 1 ? "✓ " : ""}{side1}
                                                </div>
                                                <div style={{ color: T.muted, fontSize: "10px", margin: "1px 0" }}>vs</div>
                                                <div style={{ fontWeight: match.winner_side === 2 ? "bold" : "500", color: match.winner_side === 2 ? T.darkerGreen : T.ink }}>
                                                    {match.winner_side === 2 ? "✓ " : ""}{side2}
                                                </div>
                                                {scoreText && (
                                                    <div style={{ marginTop: "4px", paddingTop: "3px", borderTop: `1px solid ${T.border}`, fontWeight: "bold", color: T.ink, textAlign: "right" }}>
                                                        {scoreText}
                                                    </div>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderGroups = () => {
        const groups = Array.isArray(data) ? data : (data?.groups || []);
        if (!groups.length) {
            return (
                <div style={styles.emptyBox}>
                    {t("No data available")}
                </div>
            );
        }

        const getMatch = (group, row, col) => {
            if (row === col) return { match: null };
            const n = group.number_players || (group.players ? group.players.length : 4);
            let index = 0;
            for (let r = 0; r < n; r++) {
                for (let c = r + 1; c < n; c++) {
                    if ((r === row && c === col) || (r === col && c === row)) {
                        return { match: group.matches?.[index] };
                    }
                    index++;
                }
            }
            return { match: null };
        };

        return (
            <div style={{ display: "flex", flexDirection: "column", gap: "25px", width: "100%" }}>
                {groups.map(group => {
                    const playersCount = group.number_players || (group.players ? group.players.length : 4);
                    const playerNumbers = Array.from({ length: playersCount }, (_, i) => i + 1);
                    const groupLabel = `${t("Group")} ${groupCharCode(group.number)} (${group.number})`;

                    // Standings calculation
                    const dynamicStats = Array.from({ length: playersCount }, () => ({ games: 0, points: 0, hasStats: false }));
                    let mIndex = 0;
                    for (let r = 0; r < playersCount; r++) {
                        for (let c = r + 1; c < playersCount; c++) {
                            const match = group.matches?.[mIndex++];
                            if (match && match.sets && match.sets.length) {
                                let g1 = 0, g2 = 0;
                                match.sets.forEach(s => {
                                    g1 += Number(s.game_score_1) || 0;
                                    g2 += Number(s.game_score_2) || 0;
                                });
                                dynamicStats[r].games += (g1 - g2);
                                dynamicStats[c].games += (g2 - g1);
                                dynamicStats[r].points += (match.winner_side === 1 ? 1 : (g1 > g2 ? 1 : 0));
                                dynamicStats[c].points += (match.winner_side === 2 ? 1 : (g2 > g1 ? 1 : 0));
                                dynamicStats[r].hasStats = true;
                                dynamicStats[c].hasStats = true;
                            }
                        }
                    }

                    return (
                        <div key={group.id || group.number} style={styles.blockCard}>
                            <div style={styles.cardHeader}>
                                {groupLabel}
                            </div>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={{ ...styles.th, width: "35px", textAlign: "center" }}>№</th>
                                        <th style={{ ...styles.th, minWidth: "180px" }}>{t("Participants")}</th>
                                        {playerNumbers.map(n => (
                                            <th key={n} style={{ ...styles.th, width: "55px", textAlign: "center" }}>{n}</th>
                                        ))}
                                        <th style={{ ...styles.th, width: "45px", textAlign: "center" }}>{t("+/-")}</th>
                                        <th style={{ ...styles.th, width: "45px", textAlign: "center" }}>{t("PTS")}</th>
                                        <th style={{ ...styles.th, width: "45px", textAlign: "center" }}>{t("Pl")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.from({ length: playersCount }, (_, rowIndex) => {
                                        const p = group.players?.[rowIndex] || group.participants?.[rowIndex];
                                        let name = `—`;
                                        if (p) {
                                            if (grade === 1) {
                                                name = formatPlayerName(p);
                                            } else if (grade === 2) {
                                                const pair = p.players || p.team?.players || (Array.isArray(p) ? p : []);
                                                name = formatDoublesPair(pair[0], pair[1]);
                                            } else {
                                                name = p.name || p.team?.name || `${t("Team")} ${rowIndex + 1}`;
                                            }
                                        }

                                        const diffVal = p?.games != null ? p.games : (dynamicStats[rowIndex].hasStats ? dynamicStats[rowIndex].games : 0);
                                        const ptsVal = p?.points != null ? p.points : (dynamicStats[rowIndex].hasStats ? dynamicStats[rowIndex].points : 0);
                                        const diff = diffVal !== 0 ? `${diffVal > 0 ? "+" : ""}${diffVal}` : "0";
                                        const pts = String(ptsVal);
                                        const place = p?.place || "—";

                                        return (
                                            <tr key={rowIndex} style={rowIndex % 2 === 1 ? styles.trAlt : {}}>
                                                <td style={{ ...styles.td, textAlign: "center", fontWeight: "bold", background: T.superWhite, color: T.ink }}>{rowIndex + 1}</td>
                                                <td style={{ ...styles.td, fontWeight: "500", color: p ? T.ink : T.muted }}>{name}</td>
                                                {playerNumbers.map((_, colIndex) => {
                                                    if (rowIndex === colIndex) {
                                                        return (
                                                            <td key={colIndex} style={{ ...styles.td, textAlign: "center", background: T.border, color: T.muted }}>
                                                                ●
                                                            </td>
                                                        );
                                                    }
                                                    const { match } = getMatch(group, rowIndex, colIndex);
                                                    if (!match || !match.sets?.length) {
                                                        return <td key={colIndex} style={{ ...styles.td, textAlign: "center", color: T.muted }}>-</td>;
                                                    }

                                                    const scoreStr = match.sets.map(set => {
                                                        const g1 = set.game_score_1 != null ? set.game_score_1 : 0;
                                                        const g2 = set.game_score_2 != null ? set.game_score_2 : 0;
                                                        return rowIndex < colIndex ? `${g1}:${g2}` : `${g2}:${g1}`;
                                                    }).join(" ");

                                                    const isWinner = match.winner_side === (rowIndex < colIndex ? 1 : 2);
                                                    const isLoser = match.winner_side && match.winner_side !== (rowIndex < colIndex ? 1 : 2);

                                                    return (
                                                        <td key={colIndex} style={{
                                                            ...styles.td,
                                                            textAlign: "center",
                                                            fontSize: "11px",
                                                            fontWeight: isWinner ? "bold" : "normal",
                                                            color: isWinner ? T.darkerGreen : isLoser ? T.alarm : T.muted
                                                        }}>
                                                            {scoreStr || "-"}
                                                        </td>
                                                    );
                                                })}
                                                <td style={{ ...styles.td, textAlign: "center", fontWeight: "500" }}>
                                                    {diff}
                                                </td>
                                                <td style={{ ...styles.td, textAlign: "center", fontWeight: "bold", color: T.ink }}>
                                                    {pts}
                                                </td>
                                                <td style={{ ...styles.td, textAlign: "center", fontWeight: "bold", color: T.ink, background: T.superWhite }}>
                                                    {place}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderParticipants = () => {
        const list = Array.isArray(data) ? data : (data?.results || []);
        if (!list.length) {
            return (
                <div style={styles.emptyBox}>
                    {t("No data available")}
                </div>
            );
        }

        return (
            <div style={{ width: "100%" }}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={{ ...styles.th, width: "40px", textAlign: "center" }}>№</th>
                            <th style={{ ...styles.th, minWidth: "220px" }}>{t("Participant")}</th>
                            <th style={{ ...styles.th, minWidth: "140px" }}>{t("City / Country")}</th>
                            <th style={{ ...styles.th, width: "80px", textAlign: "center" }}>{t("Rating")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((item, index) => {
                            let name = "—";
                            let cityCountry = "";
                            let rating = "—";
                            let flagCode = null;
                            let membersLine = "";

                            if (grade === 1) {
                                name = formatEntryListName(item);
                                flagCode = item?.country || null;
                                const countryStr = item?.country ? t(getOptionName(COUNTRY_OPTIONS, item.country)) : null;
                                cityCountry = [item?.city, countryStr && !/^\d+$/.test(String(countryStr).trim()) ? countryStr : null].filter(Boolean).join(", ");
                                rating = item?.rating != null ? item.rating : "—";
                            } else if (grade === 2) {
                                const p1 = item?.players?.[0];
                                const p2 = item?.players?.[1];
                                name = formatEntryListDoublesPair(p1, p2);
                                const rawCountry = p1?.country || p2?.country;
                                flagCode = rawCountry || null;
                                const countryStr = rawCountry ? t(getOptionName(COUNTRY_OPTIONS, rawCountry)) : null;
                                cityCountry = [p1?.city || p2?.city, countryStr && !/^\d+$/.test(String(countryStr).trim()) ? countryStr : null].filter(Boolean).join(", ");
                                const pairSum = (p1?.rating || 0) + (p2?.rating || 0);
                                rating = item?.team_rating != null ? item.team_rating : (pairSum > 0 ? pairSum : "—");
                            } else {
                                name = item?.name || `${t("Team")} ${index + 1}`;
                                membersLine = formatTeamMembers(item?.players);
                                rating = item?.team_rating || "—";
                            }

                            return (
                                <tr key={item?.id || index} style={index % 2 === 1 ? styles.trAlt : {}}>
                                    <td style={{ ...styles.td, textAlign: "center", fontWeight: "bold", background: T.superWhite, color: T.ink }}>
                                        {index + 1}
                                    </td>
                                    <td style={{ ...styles.td, fontWeight: "600", color: T.ink, letterSpacing: "0.2px" }}>
                                        <div>{name}</div>
                                        {membersLine && <div style={{ fontSize: "11px", fontWeight: 400, color: T.muted, marginTop: "2px" }}>{membersLine}</div>}
                                    </td>
                                    <td style={{ ...styles.td, color: T.muted }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                            <CountryFlag code={flagCode} />
                                            <span>{cityCountry || "—"}</span>
                                        </div>
                                    </td>
                                    <td style={{ ...styles.td, textAlign: "center", fontWeight: "bold", color: T.ink }}>
                                        {rating}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderResults = () => {
        const list = Array.isArray(data) ? data : (data?.results || []);
        if (!list.length) {
            return (
                <div style={styles.emptyBox}>
                    {t("No data available")}
                </div>
            );
        }

        return (
            <div style={{ width: "100%" }}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={{ ...styles.th, width: "60px", textAlign: "center" }}>{t("Place")}</th>
                            <th style={{ ...styles.th, minWidth: "200px" }}>{t("Participant")}</th>
                            <th style={{ ...styles.th, width: "70px", textAlign: "center" }}>{t("Matches")}</th>
                            <th style={{ ...styles.th, width: "60px", textAlign: "center" }}>{t("Wins")}</th>
                            <th style={{ ...styles.th, width: "60px", textAlign: "center" }}>{t("Losses")}</th>
                            <th style={{ ...styles.th, width: "60px", textAlign: "center" }}>{t("+/-")}</th>
                            <th style={{ ...styles.th, width: "80px", textAlign: "center" }}>{t("Rating")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((item, index) => {
                            let name = "—";
                            if (grade === 1) {
                                name = formatPlayerName(item?.player || item);
                            } else if (grade === 2) {
                                const players = item?.team?.players || item?.players || [];
                                name = formatDoublesPair(players[0], players[1]);
                            } else {
                                name = item?.team?.name || item?.name || `Team #${index + 1}`;
                            }

                            const matches = item?.tournament_matches ?? 0;
                            const wins = item?.tournament_wins ?? 0;
                            const losses = item?.tournament_losses ?? 0;
                            const points = item?.tournament_score ?? 0;
                            const place = (item?.tournament_place && item.tournament_place !== 0 && item.tournament_place !== "0") ? item.tournament_place : "—";

                            return (
                                <tr key={item?.id || index} style={index % 2 === 1 ? styles.trAlt : {}}>
                                    <td style={{ ...styles.td, textAlign: "center", fontWeight: "bold", background: T.superWhite, color: T.ink }}>
                                        {place}
                                    </td>
                                    <td style={{ ...styles.td, fontWeight: "500", color: T.ink }}>
                                        {name}
                                    </td>
                                    <td style={{ ...styles.td, textAlign: "center" }}>
                                        {matches}
                                    </td>
                                    <td style={{ ...styles.td, textAlign: "center", color: T.ink, fontWeight: "bold" }}>
                                        {wins}
                                    </td>
                                    <td style={{ ...styles.td, textAlign: "center", color: T.alarm }}>
                                        {losses}
                                    </td>
                                    <td style={{ ...styles.td, textAlign: "center" }}>
                                        {item?.games_diff != null ? `${item.games_diff > 0 ? "+" : ""}${item.games_diff}` : "—"}
                                    </td>
                                    <td style={{ ...styles.td, textAlign: "center", fontWeight: "bold", color: T.ink }}>
                                        {points}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderBrackets = () => {
        const draws = Array.isArray(data) ? data : (data?.draws || []);
        if (!draws.length) {
            return (
                <div style={styles.emptyBox}>
                    {t("No data available")}
                </div>
            );
        }

        // Row-per-player table with rowSpan-merged match columns, matching
        // the app's own live "Pro" bracket view and pro-tour draw sheets
        // (ATP/Rankedin/Challonge...) - not a flat match-by-match table,
        // which is what this used to render here (still what the Excel/
        // clipboard export uses, via buildBracketsDataset - a bracket has
        // no sane flat-file equivalent).
        return <PrintableBracketTree draws={draws} grade={grade} i18n={i18n} />;
    };

    return (
        <div style={styles.wrapper}>
            {/* Header: Name, Club, Location, Format, Stage, Date */}
            <div style={styles.header}>
              <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                {tournament?.club_logo && !logoFailed && (
                    <img
                        src={tournament.club_logo}
                        alt=""
                        crossOrigin="anonymous"
                        style={styles.clubLogo}
                        onError={() => setLogoFailed(true)}
                    />
                )}
                <div>
                <div style={styles.title}>{tournament?.name || t("Tournament")}</div>
                <div style={styles.metaRow}>
                    {club && (
                        <span style={styles.metaItem}>
                            <strong style={styles.metaLabel}>{t("Club")}:</strong> {club}
                        </span>
                    )}
                    {location && (
                        <span style={styles.metaItem}>
                            <strong style={styles.metaLabel}>{t("Location")}:</strong> {location}
                        </span>
                    )}
                    {formatStr && (
                        <span style={styles.metaItem}>
                            <strong style={styles.metaLabel}>{t("Format")}:</strong> {formatStr}
                        </span>
                    )}
                    {cleanStage && (
                        <span style={styles.metaItem}>
                            <strong style={styles.metaLabel}>{t("Stage")}:</strong>
                            <span style={styles.stageBadge}>{cleanStage}</span>
                        </span>
                    )}
                    {dateRange && (
                        <span style={styles.metaItem}>
                            <strong style={styles.metaLabel}>{t("Date")}:</strong> {dateRange}
                        </span>
                    )}
                </div>
                </div>
              </div>
            </div>

            {/* Content Table */}
            {blockKey === "schedule" && renderSchedule()}
            {blockKey === "groups" && renderGroups()}
            {blockKey === "participants" && renderParticipants()}
            {blockKey === "results" && renderResults()}
            {blockKey === "brackets" && renderBrackets()}

            {/* Footer */}
            <div style={styles.footer}>
                <div>{new Date().toLocaleDateString("uk-UA", { year: "numeric", month: "long", day: "numeric" })}</div>
                <div style={{ fontWeight: "600", color: T.ink }}>Tournaments Platform</div>
            </div>
        </div>
    );
};

const styles = {
    wrapper: {
        background: T.white,
        color: T.ink,
        padding: "24px 30px",
        fontFamily: T.fontFamily,
        minWidth: "850px",
        boxSizing: "border-box",
    },
    header: {
        borderBottom: `2px solid ${T.border}`,
        paddingBottom: "14px",
        marginBottom: "20px",
    },
    title: {
        fontSize: "22px",
        fontWeight: "bold",
        color: T.ink,
        marginBottom: "8px",
        letterSpacing: "-0.2px",
    },
    clubLogo: {
        width: "56px",
        height: "56px",
        borderRadius: "50%",
        objectFit: "cover",
        flex: "none",
        border: `1px solid ${T.border}`,
    },
    metaRow: {
        fontSize: "13px",
        color: T.muted,
        display: "flex",
        flexWrap: "wrap",
        gap: "6px 14px",
        alignItems: "center",
        lineHeight: "1.5",
    },
    metaItem: {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
    },
    metaLabel: {
        color: T.ink,
        fontWeight: "600",
    },
    stageBadge: {
        background: T.border,
        color: T.ink,
        padding: "2px 8px",
        borderRadius: "4px",
        fontWeight: "bold",
        fontSize: "12px",
        marginLeft: "2px",
    },
    blockCard: {
        border: `1px solid ${T.border}`,
        borderRadius: "6px",
        overflow: "hidden",
        background: T.white,
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    },
    cardHeader: {
        // Gray/white/uppercase - matches the vector PDF export's card
        // header bar exactly (pdfGroupsTables.js/pdfBracketTree.js); the
        // PDF has no green anywhere, this PNG path keeps its own green
        // winner-highlight color elsewhere, but the banner itself stays
        // identical between the two exports.
        background: T.headerGray,
        color: T.white,
        padding: "8px 12px",
        fontWeight: "bold",
        fontSize: "14px",
        letterSpacing: "0.2px",
        textTransform: "uppercase",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "13px",
        border: `1px solid ${T.border}`,
    },
    th: {
        background: T.superWhite,
        color: T.ink,
        padding: "8px 10px",
        fontWeight: "700",
        border: `1px solid ${T.border}`,
        textAlign: "left",
    },
    td: {
        padding: "7px 10px",
        border: `1px solid ${T.border}`,
        // default/fallback text color for any cell that doesn't override it -
        // T.muted (--secondary-color) is too low-contrast on white
        // (~2.5:1) to be a body-text default, even though it's the real
        // token; that role's real equivalent here is T.ink (--primary-color).
        color: T.ink,
    },
    trAlt: {
        background: T.superWhite,
    },
    emptyBox: {
        padding: "20px",
        textAlign: "center",
        color: T.muted,
        background: T.superWhite,
        border: `1px dashed ${T.border}`,
        borderRadius: "6px",
    },
    footer: {
        marginTop: "24px",
        paddingTop: "12px",
        borderTop: `1px solid ${T.border}`,
        display: "flex",
        justifyContent: "space-between",
        fontSize: "12px",
        color: T.muted,
    }
};

export default TournamentPrintableTable;
